import { type ComponentProps, type ReactNode, useState } from 'react';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '@nesso/ui/primitives/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@nesso/ui/primitives/tooltip';

interface PopoverTooltipButtonProps {
    tooltipContent?: ReactNode;
    popoverContent?: ReactNode;
    children: ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    buttonProps?: ComponentProps<typeof Button>;
    popoverProps?: ComponentProps<typeof Popover>;
    tooltipProps?: ComponentProps<typeof Tooltip>;
    tooltipContentProps?: ComponentProps<typeof TooltipContent>;
    popoverContentProps?: ComponentProps<typeof PopoverContent>;
    wrapPopoverContent?: boolean;
    hideTooltipWhenOpen?: boolean;
}

export function PopoverTooltipButton({
    tooltipContent,
    popoverContent,
    children,
    open,
    onOpenChange,
    buttonProps,
    popoverProps,
    tooltipProps,
    tooltipContentProps,
    popoverContentProps,
    wrapPopoverContent = true,
    hideTooltipWhenOpen = true,
}: PopoverTooltipButtonProps) {
    const hasPopover = popoverContent !== undefined;
    const hasTooltip = tooltipContent !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : internalOpen;

    const canShowTooltip = hasTooltip && (!hideTooltipWhenOpen || !isOpen);
    const openRingClass = isOpen ? 'ring-2 ring-accent' : undefined;

    const mergedButtonProps: ComponentProps<typeof Button> = {
        ...buttonProps,
        onPointerEnter: (e) => {
            buttonProps?.onPointerEnter?.(e);
            if (canShowTooltip) setTooltipOpen(true);
        },
        onPointerLeave: (e) => {
            buttonProps?.onPointerLeave?.(e);
            setTooltipOpen(false);
        },
        onPointerDown: (e) => {
            buttonProps?.onPointerDown?.(e);
            setTooltipOpen(false);
        },
        onFocus: (e) => {
            buttonProps?.onFocus?.(e);
            setTooltipOpen(false);
        },
        onBlur: (e) => {
            buttonProps?.onBlur?.(e);
            setTooltipOpen(false);
        },
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!isControlled) setInternalOpen(nextOpen);
        onOpenChange?.(nextOpen);

        if (!nextOpen && hasTooltip) {
            setTooltipOpen(false);
        }
    };

    if (!hasPopover) {
        if (!hasTooltip) {
            return (
                <Button {...buttonProps} className={cn(buttonProps?.className)}>
                    {children}
                </Button>
            );
        }

        return (
            <Tooltip open={canShowTooltip ? tooltipOpen : false} {...tooltipProps}>
                <TooltipTrigger asChild>
                    <Button {...mergedButtonProps} className={cn(mergedButtonProps?.className)}>
                        {children}
                    </Button>
                </TooltipTrigger>
                <TooltipContent {...tooltipContentProps}>{tooltipContent}</TooltipContent>
            </Tooltip>
        );
    }

    if (!hasTooltip) {
        return (
            <Popover open={isOpen} onOpenChange={handleOpenChange} {...popoverProps}>
                <PopoverTrigger asChild>
                    <Button {...buttonProps} className={cn(openRingClass, buttonProps?.className)}>
                        {children}
                    </Button>
                </PopoverTrigger>
                {wrapPopoverContent ? (
                    <PopoverContent
                        onCloseAutoFocus={(e) => e.preventDefault()}
                        onPointerDownOutside={(e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest('button')) {
                                e.preventDefault();
                            }
                        }}
                        {...popoverContentProps}
                    >
                        {popoverContent}
                    </PopoverContent>
                ) : (
                    popoverContent
                )}
            </Popover>
        );
    }

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange} {...popoverProps}>
            <Tooltip open={canShowTooltip ? tooltipOpen : false} {...tooltipProps}>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <Button
                            {...mergedButtonProps}
                            className={cn(openRingClass, mergedButtonProps?.className)}
                        >
                            {children}
                        </Button>
                    </PopoverTrigger>
                </TooltipTrigger>
                {(!hideTooltipWhenOpen || !isOpen) && (
                    <TooltipContent {...tooltipContentProps}>{tooltipContent}</TooltipContent>
                )}
            </Tooltip>
            {wrapPopoverContent ? (
                <PopoverContent
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    onPointerDownOutside={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('button')) {
                            e.preventDefault();
                        }
                    }}
                    {...popoverContentProps}
                >
                    {popoverContent}
                </PopoverContent>
            ) : (
                popoverContent
            )}
        </Popover>
    );
}
